import React, { useState, useEffect } from 'react';
import { Card, Grid, IconButton, Radio, RadioGroup, FormControlLabel, Tooltip, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { useTheme } from '@mui/material/styles';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db, onSnapshot, auth, orderBy } from "../firebaseConfig";

const sections = (theme) => [
  {
    title: 'What is Forex? The Basics Explained',
    content: (
      <MDBox>
        <MDTypography variant="body1" color="text" mb={2}>
          Forex, or foreign exchange, is the global marketplace where currencies are traded. Think of it like exchanging dollars for euros at an airport—but instead of traveling, traders aim to profit from changes in currency prices. Forex is the world’s largest financial market, operating 24 hours a day, five days a week (Monday to Friday).
        </MDTypography>
        <MDTypography variant="body1" color="text" mb={2}>
          Why do currency prices move? Prices change due to supply and demand, driven by economic data (like jobs reports or inflation), central bank decisions (like interest rate changes), and global events (like political news). For example, if the US economy strengthens, the dollar may rise against the euro.
        </MDTypography>
        <MDTypography variant="body1" color="text" mb={2}>
          You’ll need a broker to trade forex. A broker is a company that connects you to the market, providing a platform to place trades and view charts. They charge fees like spreads or commissions. Choosing a trusted broker is key to safe trading, and most offer demo accounts to practice with virtual money—no real risk!
        </MDTypography>
        <MDTypography variant="body1" color="text" mb={2}>
          Key terms to understand:
        </MDTypography>
        <ul style={{ color: theme.palette.text.default, paddingLeft: '20px', marginBottom: '16px' }}>
          <li><strong>Currency Pairs</strong>: Currencies are traded in pairs, like EUR/USD (euro vs. US dollar). If you think the euro will rise, you buy EUR/USD; if you think the dollar will rise, you sell it.</li>
          <li><strong>Pips</strong>: A pip is a small price change, usually 0.0001 for most pairs. Example: If EUR/USD moves from 1.1000 to 1.1001, that’s 1 pip.</li>
          <li><strong>Lots</strong>: A lot represents the size of your trade.
            <br />
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li><strong>Standard lot</strong>: 1.00 lot = 100,000 units of currency (1 pip ≈ $10 for EUR/USD)</li>
              <li><strong>Mini lot</strong>: 0.10 lot = 10,000 units (1 pip ≈ $1)</li>
              <li><strong>Micro lot</strong>: 0.01 lot = 1,000 units (1 pip ≈ $0.10)</li>
              <li>For example, if you trade 0.10 lots of EUR/USD and gain 15 pips, your profit is roughly $15 (since 1 pip = $1 for a mini lot in a US dollar account).</li>
            </ul>
          </li>
          <li><strong>Leverage</strong>: Leverage lets you trade more than you have. With 10:1 leverage, $100 controls $1,000, but be warned it increases both gains and losses.</li>
          <li><strong>Market Sessions</strong>: The forex market runs 24 hours a day through four major trading sessions:
            <br />
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li><strong>Sydney</strong>: 10:00 PM – 7:00 AM UTC</li>
              <li><strong>Tokyo</strong>: 12:00 AM – 9:00 AM UTC</li>
              <li><strong>London</strong>: 8:00 AM – 5:00 PM UTC</li>
              <li><strong>New York</strong>: 1:00 PM – 10:00 PM UTC</li>
              <li>These sessions overlap, especially London and New York (1:00 PM – 5:00 PM UTC), which is usually the most active and volatile trading period.</li>
            </ul>
          </li>
          <li><strong>Why Trade Forex</strong>: Forex trading involves buying one currency while selling another, aiming to profit from changes in exchange rates. As the world’s largest and most liquid financial market, it offers opportunities for both short-term speculation and long-term strategies. With 24-hour access and relatively low entry barriers, many traders use forex to supplement their income or build a trading career.</li>
        </ul>
        <MDTypography variant="body1" color="text" mb={2}>
          Example: If you buy 0.1 lots of EUR/USD at 1.1000 and it rises to 1.1020, you gain 20 pips. For 0.1 lots, 1 pip usually equals $1, so that’s a $20 gain.
        </MDTypography>
        <MDTypography variant="body1" color="text" mb={2}>
          Now that you know the basics, let’s learn how to read price charts to spot trading opportunities.
        </MDTypography>
      </MDBox>
    ),
    quiz: [
      { 
        question: 'What is forex?', 
        options: ['Trading stocks', 'Trading currencies', 'Trading bonds'], 
        answer: 1
      },
      { 
        question: 'What is a pip?', 
        options: ['A trade size', 'A small price change', 'A currency pair'], 
        answer: 1
      },
      { 
        question: 'What does EUR/USD mean?', 
        options: ['Euro vs. Yen', 'Dollar vs. Euro', 'Euro vs. Dollar'], 
        answer: 2
      },
      { 
        question: 'What is a standard lot?', 
        options: ['1,000 units', '100,000 units', '10,000 units'], 
        answer: 1
      },
      { 
        question: 'Which forex trading session is typically the most active?', 
        options: ['London-New York overlap', 'Sydney-Tokyo overlap', 'Tokyo-London overlap'], 
        answer: 0
      },
      { 
        question: 'What are the typical hours of the London forex session (in UTC)?', 
        options: ['2 PM – 10 PM', '12 AM – 9 AM', '8 AM – 5 PM'], 
        answer: 2
      },
      { 
        question: 'What drives currency price changes?', 
        options: ['Fixed daily rates', 'Only broker fees', 'Supply, demand, and economic events'], 
        answer: 2
      },
      { 
        question: 'What is a demo account used for?', 
        options: ['Tax calculations', 'Real money trading', 'Practicing with virtual money'], 
        answer: 2
      }
    ]
  },
  {
    title: 'Understanding Charts: Your Guide to Prices',
    content: (
      <MDBox>
        <MDTypography variant="body1" color="text" mb={2}>
          Charts show how prices move over time, helping you decide when to trade. Think of them as graphs tracking currency values. You can view charts in our backtesting software (powered by TradingView’s charting) or on platforms like MetaTrader 4/5 (free with most brokers).
        </MDTypography>
        <ul style={{ color: theme.palette.text.default, paddingLeft: '20px', marginBottom: '16px' }}>
          <li><strong>Line Charts</strong>: Show closing prices over time with a single line. Good for spotting trends but lacks detail.</li>
          <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/linechart.png"
            alt="Line Chart Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>
          <li>
            <strong>Candlestick Charts</strong>: The most common chart to trade with, showing open, high, low, and close prices for a time period. Each candlestick represents one unit of the timeframe you’re looking at (e.g., 1 hour). Each candlestick includes:
            <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/candlechart.png"
            alt="Candlestick Chart Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li><strong>Body</strong>: The thick part shows the open and close prices (the range).</li>
              <li><strong>Wicks (Shadows)</strong>: Thin lines above and below the body show the highest and lowest prices during that time period.</li>
              <li><strong>Color</strong>: Green (or white) means the price closed higher than it opened (bullish); red (or black) means it closed lower (bearish). Note — colors may vary by platform and can be customized.</li>
            </ul>
          </li>
          <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/Candlestick.png"
            alt="Candlestick Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>
          <li><strong>Volume</strong>: Shows how many trades happened during the time period, indicating the strength of price moves.</li>
          <li><strong>Timeframes</strong>: Charts can show 1 minute (M1), 15 minutes (M15), 1 hour (H1), or 1 day (D1). Short timeframes like M1 or M15 suit quick trades (scalping or intra-day), while longer ones like H1 or D1 are for bigger moves (swing trading). Beginners often start with H1 for clarity. However, some traders use the higher time frames to confirm direction whilst using the lower timeframes for entries.</li>
          <li><strong>Trends</strong>: Prices move up (bullish), down (bearish), or sideways (range). A bullish trend means prices are rising.</li>
          <li>
            <strong>Chart Patterns</strong>: Prices form shapes that hint at future moves, making them a popular way to trade (but not the only way—indicators or price action are also effective). Common patterns include:
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li><strong>Triangles</strong>: Prices tighten into a triangle shape, often signaling a breakout (sharp move up or down). Example: If EUR/USD forms a symmetrical triangle on a 4H chart, a breakout above the upper line suggests a buy.</li>
              <li><strong>Double Top/Bottom</strong>: A double top looks like an "M" and suggests a price drop after hitting resistance twice. A double bottom looks like a "W" and suggests a price rise after hitting support. Example: GBP/USD hits 1.3000 twice and falls, forming a double top.</li>
              <li><strong>Head and Shoulders</strong>: A pattern with three peaks (left shoulder, head, right shoulder) that signals a reversal. Example: A head and shoulders on USD/JPY at 150.00 suggests a bearish move.</li>
              <li><em>Note</em>: Chart patterns are one approach to trading. You can also use indicators (like RSI) or raw price action. Test different methods in our backtesting software to find what suits you.</li>
            </ul>
            <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/chartpattens.png"
            alt="Pattern Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>
          </li>
          <li><strong>Entry</strong>: The price level where you open a trade.</li>
          <li><strong>Exit</strong>: Where you close your trade to take profits or cut losses.</li>
          <li><strong>Stop Loss</strong>: A preset price to limit losses if the market moves against you. It’s a great risk management tool.</li>
          <li><strong>Take Profit</strong>: A preset price where you automatically close the trade to secure profits, so you don’t have to keep watching.</li>
          <li><strong>Break Even</strong>: Moving your stop loss to your entry price so you don’t lose money on a trade. Useful when the trade is already in profit.</li>
          <li><strong>Volatility</strong>: Refers to how much prices move up and down. High volatility means bigger swings and higher risk/reward. This is shown by bigger candlesticks.</li>
        </ul>
        <MDTypography variant="body1" color="text" mb={2}>
          Below shows a typical order form, which displays the entry and exit and the price of the currency.
        </MDTypography>
        <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/ordervalues.png"
            alt="Pattern Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>
        <MDTypography variant="body1" color="text" mb={2}>
          Example: On a 1-hour candlestick chart, if EUR/USD shows many green candlesticks (closing higher than opening), it’s a bullish trend. A double bottom pattern at 1.1000 might signal a buy opportunity.
        </MDTypography>

        <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/bullishtrend.png"
            alt="Bullish Trend Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>
        <MDTypography variant="body1" color="text" mb={2}>
          Now that you can read charts, let’s explore tools to make smart trading decisions.
        </MDTypography>
      </MDBox>
    ),
    quiz: [
      { question: 'What does a candlestick chart show?', options: ['Only closing prices', 'Open, high, low, close prices', 'Volume only'], answer: 1 },
      { question: 'What does each candlestick represent?', options: ['Only the closing price at the end of the timeframe', 'The opening and closing prices within the timeframe', 'The full price range: open, high, low, and close prices during the timeframe'], answer: 2 },
      { question: 'What is the purpose of the wick on a candlestick?', options: ['Show volume', 'Show highest and lowest prices', 'Show trade size'], answer: 1 },
      { question: 'What does high volume usually indicate?', options: ['Strong price move', 'Price reversal', 'No change'], answer: 0 },
      { question: 'What is a stop loss?', options: ['A target to take profit', 'A limit to cut losses', 'A chart pattern'], answer: 1 },
      { question: 'What does "break even" mean?', options: ['No profit or loss', 'Big profit', 'Big loss'], answer: 0 },
      { question: 'What is volatility?', options: ['Price stability', 'Price movement size', 'Trade volume'], answer: 1 },
      { question: 'What does H1 mean?', options: ['1-hour chart', '1-day chart', '1-minute chart'], answer: 0 },
      { question: 'What is a line chart good for?', options: ['Detailed price action', 'Spotting trends', 'Volume analysis'], answer: 1 },
      { question: 'What is a chart pattern?', options: ['A random price move', 'A type of indicator', 'A shape prices form that hints at future moves'], answer: 2 },
    ],
  },
  {
    title: 'Trading Tools: Making Smart Decisions',
    content: (
      <MDBox>
        <MDTypography variant="body1" color="text" mb={2}>
          Trading successfully isn’t just about luck or gut feeling—it’s about using the right tools to plan, manage risk, and stay disciplined. Here are essential tools to help you trade smarter:
        </MDTypography>
        <ul style={{ color: theme.palette.text.default, paddingLeft: '20px', marginBottom: '16px' }}>
          <li>
            <strong>Long Position</strong>: You buy a currency pair expecting the price to rise. For example, buy EUR/USD at 1.1000, and sell at 1.1020 to make a 20-pip profit ($20 if 0.01 lot size is used).
          </li>
          <li>
            <strong>Short Position</strong>: You sell a currency pair expecting the price to fall. For example, sell GBP/USD at 1.3000 and buy it back at 1.2950 to profit from the drop.
          </li>
          <li>
            <strong>Stop-Loss</strong>: An automatic order to exit a losing trade at a certain price. This protects your account from large losses. For example, if you buy at 1.1000, you might place a stop-loss at 1.0980 to limit your risk to 20 pips.
          </li>
          <li>
            <strong>Take-Profit</strong>: An automatic order to close a trade at your target price to secure profits. Example: Buy at 1.1000, take profit at 1.1040 for a 40-pip gain.
          </li>
          <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/ordervalues.png"
            alt="Pattern Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>
          <li>
            <strong>Risk-to-Reward Ratio (R:R)</strong>: This measures how much you’re risking vs. how much you expect to gain.
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li>A 1:2 R:R means risking $100 to potentially make $200.</li>
              <li>You don’t need to win every trade—with a 1:2 ratio, you only need to win 34% of trades to be profitable long term.</li>
              <li>This is key for managing expectations and avoiding emotional overtrading.</li>
            </ul>
          </li>
          <li>
            <strong>1–2% Risk Rule</strong>: This rule says you should only risk 1–2% of your total account balance on any single trade.
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li>If you have $1,000, a 2% risk means risking only $20 per trade (so you would use 0.01 lot sizing).</li>
              <li>This prevents large losses during losing streaks and keeps you in the game longer.</li>
              <li>Even professional traders follow this—staying consistent matters more than trying to win big fast.</li>
            </ul>
          </li>
          <li>
            <strong>Indicators</strong>: These help you analyze market behavior and identify possible trade setups:
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li><strong>Moving Average (MA)</strong>: Smooths out price data to help spot overall trends (The example below shows the MA (orange line)).</li>
              <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/MA.png"
            alt="MACD Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>
              <li><strong>RSI (Relative Strength Index)</strong>: Measures whether a market is overbought (above 70) or oversold (below 30), helping you avoid late entries (Below shows the RSI on the lower part of the chart).</li>
              <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/RSI.jpg"
            alt="RSI Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>
              <li><strong>MACD (Moving Average Convergence Divergence)</strong>: Helps identify momentum and potential trend reversals(Below shows the MACD on the lower part of the chart).</li>
              <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/macd.png"
            alt="MACD Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>
            </ul>
          </li>
          <li>
            <strong>Trade Journaling</strong>: Logging your trades, reasons behind them, emotions felt, and the outcomes.
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li>Helps you identify emotional patterns like fear or greed that lead to bad decisions.</li>
              <li>For example, if you notice you’re always closing trades early out of fear, you can work on that behavior.</li>
              <li><em>Fact:</em> Studies show over 70% of beginner traders lose money—often due to poor emotional control. Journaling helps reduce that risk. FX Bullsheet even has trade replay, letting AI review each trade you make!</li>
              <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/journal.png"
            alt="RSI Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>
            </ul>
          </li>
          <li>
            <strong>Backtesting</strong>: Testing your strategy on historical data to see how it would have performed.
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li>Allows you to simulate weeks or months of trading in minutes—saving time and exposing flaws early.</li>
              <li>Boosts confidence by showing your strategy has logic behind it.</li>
              <li>Example: Use our backtesting software, scroll back on a chart (e.g., EUR/USD H1), apply your strategy rules (e.g., buy at support), and record entries, exits, and results for 50–100 trades.</li>
              <li><em>After this course:</em> Spend a few hours each week backtesting new strategies on different pairs and timeframes. The more you test, the better you’ll trade. Once you have one that works, stick with it!</li>
            </ul>
            <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/FXBBacktesting.png"
            alt="RSI Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>
          </li>
          <li>
            <strong>Risk Management</strong>: Planning how much to risk per trade, using tools like stop-loss, position sizing, and account balance limits.
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li>Protects your capital during losing streaks.</li>
              <li>Combining this with a solid R:R and the 2% rule gives you a structured and safe approach to trading.</li>
            </ul>
          </li>
          <li>
            <strong>Spreads</strong>: The difference between the bid (sell) and ask (buy) price. This is a cost of trading.
            <br />
            <em>Example:</em> If EUR/USD has a bid price of 1.1000 and an ask price of 1.1002, the spread is 2 pips. You start a trade slightly "behind" due to this cost.
          </li>
          <li>
            <strong>Slippage</strong>: The difference between the expected price when you place an order and the actual execution price. This happens often in fast or volatile markets, like during major news releases.
            <br />
            <em>Example:</em> You set a market order to buy GBP/USD at 1.3000, but due to volatility, it executes at 1.3005, costing you an extra 5 pips.
          </li>
          <li>
            <strong>Swap/Rollover Fees</strong>: Interest paid or earned for holding positions overnight, based on the interest rate differential between currencies. Some brokers also charge or credit these fees daily.
          </li>
          <li>
            <strong>Trading Costs</strong>: Beyond spreads and slippage, brokers may charge commissions (e.g., $5 per trade) or swap fees for overnight positions. Check your broker’s fee structure, and note that profits may be taxed, depending on your country.
          </li>
        </ul>
        <MDTypography variant="body1" color="text" mb={2}>
          Example: If the RSI on GBP/USD is 75, it may signal the market is overbought—suggesting a potential price drop. Confirm this with price action (e.g., a reversal at resistance), then consider a short position. Pairing it with a 1:2 risk-reward ratio and a clear stop-loss and take-profit keeps the trade structured and reduces emotional decision-making.
        </MDTypography>
        <MDTypography variant="body1" color="text" mb={2}>
          With these tools, you’re ready to learn trading strategies to plan your trades.
        </MDTypography>
      </MDBox>
    ),
    quiz: [
      { 
        question: 'What is a long position?', 
        options: ['Buying a pair', 'Holding a pair', 'Selling a pair'], 
        answer: 0
      },
      { 
        question: 'What does a stop-loss do?', 
        options: ['Limits loss', 'Locks profit', 'Increases leverage'], 
        answer: 0
      },
      { 
        question: 'What does RSI above 70 mean?', 
        options: ['Neutral', 'Oversold', 'Overbought'], 
        answer: 2
      },
      { 
        question: 'What is trade journaling used for?', 
        options: ['Tracking your trades and emotions', 'Calculating pip value', 'Predicting news'], 
        answer: 0
      },
      { 
        question: 'What is backtesting?', 
        options: ['Testing strategies on past data', 'Watching live charts', 'Guessing future prices'], 
        answer: 0
      },
      { 
        question: 'What does a 1:2 risk-to-reward ratio mean?', 
        options: ['Equal profit and loss', 'Risking $1 to make $2', 'Risking $2 to make $1'], 
        answer: 1
      },
      { 
        question: 'How much of your account should you risk per trade using the 1-2% rule?', 
        options: ['All of it', '1–2%', 'Half of it'], 
        answer: 1
      },
      { 
        question: 'Why is risk management important?', 
        options: ['To place bigger trades', 'To win more trades', 'To protect your capital'], 
        answer: 2
      },
      { 
        question: 'What is a trading cost besides spreads?', 
        options: ['Chart pattern fees', 'Commissions or swap fees', 'Broker’s platform fee'], 
        answer: 1
      }
    ]
  },
  {
    title: 'Trading Strategies: Your Plan to Win',
    content: (
      <MDBox>
        <MDTypography variant="body1" color="text" mb={2}>
          A strategy is a repeatable plan for when to buy or sell. Without one, you’re just guessing—and guessing is how most traders lose. A solid strategy uses either <strong>technical analysis</strong>, <strong>fundamental analysis</strong>, or a mix of both to help you make confident, consistent decisions.
        </MDTypography>
        <MDTypography variant="h6" color="text" mb={1}>📊 Technical vs. Fundamental Strategies</MDTypography>
        <ul style={{ color: theme.palette.text.default, paddingLeft: '20px', marginBottom: '16px' }}>
          <li><strong>Technical Analysis</strong>: Uses chart patterns, price action, and indicators to predict moves. Works best for short-term and intraday traders.</li>
          <li><strong>Fundamental Analysis</strong>: Focuses on economic news, interest rates, and geopolitical events. Great for long-term direction and macro trends.</li>
          <li>Most forex traders lean on <strong>technical strategies</strong> for precise entries and exits. Fundamentals may affect bigger trends or sudden spikes.</li>
          <li><strong>Example</strong>: A technical trader might buy when RSI is low and price touches support. A fundamental trader might buy GBP if the Bank of England raises interest rates.</li>
          <li><strong>Other Strategies</strong>: You might try trend-following (trading with the market’s direction) or range trading (buying low and selling high in a flat market). Explore these strategies in YouTube videos or our backtesting software to find what suits you.</li>
        </ul>
        <MDTypography variant="h6" color="text" mt={2} mb={1}>🧠 Price Action, Support & Resistance, and Liquidity</MDTypography>
        <ul style={{ color: theme.palette.text.default, paddingLeft: '20px', marginBottom: '16px' }}>
          <li><strong>Price Action</strong>: Reading the story that candlesticks tell—where price is struggling or breaking through. No indicators, just raw price movement.</li>
          <li><strong>Support & Resistance</strong>: Support is a price level where prices often stop falling and bounce up, like a floor. Resistance is where prices often stop rising and fall back, like a ceiling. Zoom out on a chart (e.g., H1 or D1) to find these levels where prices repeatedly turn. Example: If EUR/USD bounces at 1.1000 multiple times, that’s a support level.</li>
          <li><strong>Liquidity</strong>: Liquidity means there are enough buyers and sellers at a price to make trading easy. Big traders, like banks, target support or resistance levels where many orders sit (e.g., stop-losses). They may push prices to these levels to trigger those orders, creating a brief price spike before reversing. Watch these levels for trade setups, but always use a stop-loss.</li>
        </ul>
        <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/keylevels.png"
            alt="Support & Resistance Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>
        <MDTypography variant="h6" color="text" mt={2} mb={1}>🛠 Strategy Examples</MDTypography>
        <ul style={{ color: theme.palette.text.default, paddingLeft: '20px', marginBottom: '16px' }}>
          <li><strong>Liquidity Grab Strategy</strong>: Trade at key price levels where large orders cluster, like support or resistance. No strategy works every time—this may fail if the market moves unexpectedly due to news or strong trends, so always use a stop-loss.
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li><strong>Steps</strong>: 1) Identify a clean support level (e.g., EUR/USD at 1.1000). 2) Wait for price to drop briefly to that level (shown by a candlestick’s wick, the thin line showing the price range). 3) Enter after confirmation (e.g., a bullish candlestick). 4) Place stop-loss below (e.g., 1.0980) and take-profit above (e.g., 1.1040).</li>
              <li><strong>Example</strong>: EUR/USD dips to 1.1000 on a 4H chart. You buy 0.1 lots, risking 20 pips, targeting 60 pips for a 1:3 reward.</li>
            </ul>
          </li>
          <li><strong>Supply and Demand Strategy</strong>: Trade when price returns to zones where big moves originated (institutional interest). These zones can be broken by strong news, so test thoroughly.
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li><strong>Steps</strong>: 1) Mark a demand zone where price previously surged (e.g., GBP/USD at 1.3000). 2) Wait for price to return. 3) Buy at the zone. 4) Stop-loss below (1.2970), take-profit near recent highs (1.3100).</li>
              <li><strong>Example</strong>: GBP/USD returns to 1.3000. Buy 0.1 lots with 30 pip stop, 100 pip target—over 3:1 reward ratio.</li>
            </ul>
          </li>
        </ul>
        <MDTypography variant="h6" color="text" mt={2} mb={1}>💼 Finding a Strategy That Works</MDTypography>
        <ul style={{ color: theme.palette.text.default, paddingLeft: '20px', marginBottom: '16px' }}>
          <li>Explore strategies from YouTube, books, trading communities, or mentors (We have a list of reccomended traders to learn from on our website). Understand the logic behind them.</li>
          <li>Try different approaches—trend-following, mean reversion, breakout strategies, etc. Keep it simple.</li>
          <li><strong>Use our backtesting software</strong> to test different strategies side by side, and the AI assistant to help refine or make suggestions based on your data and risk profile.</li>
          <li>Once you’ve found a promising setup, move on to testing...</li>
        </ul>
        <MDTypography variant="h6" color="text" mt={2} mb={1}>🔁 Backtest Before You Risk Real Money</MDTypography>
        <ul style={{ color: theme.palette.text.default, paddingLeft: '20px', marginBottom: '16px' }}>
          <li>Use our backtesting software to manually test your strategy. Does it hold up over 50–100 trades?</li>
          <li>Record entries, exits, win/loss, and reward:risk ratio in the <strong>FX Bullsheet Journal</strong>.</li>
          <li>Check if the strategy is consistent. Is it profitable over time? What’s the drawdown?</li>
        </ul>
        <MDTypography variant="h6" color="text" mt={2} mb={1}>💰 Risk Management = Survival</MDTypography>
        <MDTypography variant="body1" color="text" mb={2}>
          Never risk more than <strong>1–2%</strong> of your account per trade. If you have $1,000, risk $10–20 per trade. That way, you can lose multiple trades and still recover.
        </MDTypography>
        <MDTypography variant="h6" color="text" mt={2} mb={1}>🧠 Managing Your Mind</MDTypography>
        <ul style={{ color: theme.palette.text.default, paddingLeft: '20px', marginBottom: '16px' }}>
          <li><strong>Discipline</strong>: Follow your trading plan strictly; avoid impulsive trades.</li>
          <li><strong>Patience</strong>: Wait for good setups rather than chasing every move.</li>
          <li><strong>Emotions</strong>: Fear and greed can cloud judgment. Emotions like fear of missing out (FOMO) or revenge trading (chasing losses) can ruin trades. Build a routine: trade at set times, review your journal daily, and stick to your 1–2% risk rule to stay calm.</li>
        </ul>
        <MDTypography variant="h6" color="text" mt={2} mb={1}>🛠 Additional Important Concepts</MDTypography>
        <ul style={{ color: theme.palette.text.default, paddingLeft: '20px', marginBottom: '16px' }}>
          <li>
            <strong>Drawdown</strong>: The reduction of your trading account equity from a peak to a trough during a trading period. It measures the downside risk of your trading strategy.
            <br />
            <em>Example:</em> If your account grows from $10,000 to $12,000 but then falls to $9,000, the drawdown is $3,000 or 25%.
          </li>
          <li>
            <strong>Order Types</strong>:
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li><strong>Market Orders</strong>: Executed immediately at the best available price. Use when you want to enter or exit quickly. For example, entering a trade as soon as a breakout happens.</li>
              <li><strong>Limit Orders</strong>: Set a specific price to buy or sell. The order only executes if the market reaches that price. Useful for entering at better prices or taking profits. For example, setting a buy limit order below current price to catch a dip.</li>
              <li><strong>Stop Orders</strong>: Triggered when the price hits a certain level, often used to limit losses or enter breakout trades.</li>
              <li><strong>Trailing Stops</strong>: A dynamic stop loss that moves with the market price to lock in profits while limiting downside.</li>
              <li><strong>Partial Take Profit</strong>: Closing part of your open position to lock in profits.
                <br />
                <em>Example:</em> If you’ve made $40 so far, you might close 50% of the trade (e.g., $20) to secure some profit while letting the remaining position continue to trade.
              </li>
            </ul>
          </li>
          <li>
            <strong>Choosing a Broker</strong>: Look for brokers regulated by trusted authorities (e.g., FCA, CFTC, ASIC), with transparent fees, good execution speeds, and demo accounts to practice without risk.
            <br />
            Some reputable brokers include:
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li><strong>IG Group</strong>: Well-regulated, offers a wide range of instruments and strong educational resources.</li>
              <li><strong>OANDA</strong>: Known for tight spreads and robust trading platforms.</li>
              <li><strong>Interactive Brokers</strong>: Favored by professionals, offers advanced tools and global market access.</li>
            </ul>
          </li>
          <li>
            <strong>Prop Firms (Proprietary Trading Firms)</strong>: These firms provide traders with capital to trade, often sharing profits but not risking their own money. They usually require passing evaluation programs.
            <br />
            <em>Benefits:</em> You can trade larger amounts with less personal capital, get access to professional tools and mentorship, and avoid risking your own money beyond the evaluation.
            <br />
            <em>Popular Prop Firms:</em>
            <ul style={{ color: theme.palette.text.default, paddingLeft: '20px' }}>
              <li><strong>FTMO</strong>: Offers funded accounts after passing a challenge with clear rules and targets.</li>
              <li><strong>The5ers</strong>: Focuses on low-risk trading and scaling up capital.</li>
              <li><strong>TopOneTrader</strong>: Provides funding for forex traders after evaluation.</li>
            </ul>
          </li>
        </ul>
        <MDTypography variant="caption" color="text" sx={{ textAlign: 'center', display: 'block' }}>
  Below Shows a Text Book Liquidity Grab to a Short Position 
</MDTypography>
        <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/liquidity.png"
            alt="Liquidity Grab Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>

        <MDTypography variant="body1" color="text" mb={2}>
          Now, let’s put it all together by practicing a trade setup in our backtesting software.
        </MDTypography>
      </MDBox>
    ),
    quiz: [
      { 
        question: 'What is technical analysis based on?', 
        options: ['Economic news', 'Price charts and indicators', 'Interest rates'], 
        answer: 1 
      },
      { 
        question: 'What does a liquidity grab strategy look for?', 
        options: ['Support/resistance areas with trapped orders', 'Random spikes', 'Trend lines only'], 
        answer: 0 
      },
      { 
        question: 'What’s the goal of backtesting?', 
        options: ['Copy trades', 'Watch live news', 'See how your strategy performs over time'], 
        answer: 2 
      },
      { 
        question: 'How much should you risk per trade?', 
        options: ['1-2%', '5-10%', '20%'], 
        answer: 0 
      },
      { 
        question: 'What can help you stay calm in live trading?', 
        options: ['Having a tested plan', 'Trading randomly', 'Ignoring your rules'], 
        answer: 0 
      },
      { 
        question: 'What is a support level?', 
        options: ['Where price moves sideways', 'Where price breaks out', 'Where price often bounces up'], 
        answer: 2 
      },
      { 
        question: 'What is price action?', 
        options: ['Using economic data only', 'Focusing on indicators', 'Trading based on raw price movement'], 
        answer: 2 
      },
      { 
        question: 'What is the purpose of journaling trades?', 
        options: ['Time the news', 'Track and learn from your trading behavior', 'Brag about wins'], 
        answer: 1 
      },
      { 
        question: 'What can cause a strategy to fail?', 
        options: ['Using too many indicators', 'Unexpected news or strong trends', 'Always works'], 
        answer: 1 
      }
    ]
  },
  {
    title: 'Practice a Trade: Test Your Skills',
    content: (
      <MDBox>
        <MDTypography variant="body1" color="text" mb={2}>
          Now it’s time to apply what you’ve learned by setting up a trade in our backtesting software (powered by TradingView’s charting). First, backtest your strategy using historical charts to ensure it’s consistent over 50–100 trades. Once you’re confident in your strategy, move to demo trading on a platform like MetaTrader 4/5 (free with most brokers) to practice with virtual money. Use the checklist below to guide your trade setup in the backtesting software, ensuring it follows your strategy rules.
        </MDTypography>
        <MDTypography variant="h6" color="text" mt={2} mb={1}>📋 Trade Setup Checklist</MDTypography>
        <ul style={{ color: theme.palette.text.default, paddingLeft: '20px', marginBottom: '16px' }}>
          <li>Open our backtesting software to analyze historical charts or a demo account on MetaTrader 4/5 for practice with virtual money.</li>
          <li>Pick a currency pair (e.g., EUR/USD) and a timeframe (e.g., H1 for beginners).</li>
          <li>Analyze the chart: Look for a trend (bullish or bearish), a key level (e.g., support or resistance), or a chart pattern (e.g., double bottom).</li>
          <li>Decide to buy (long) or sell (short) based on your analysis. For example, buy if the price bounces off support with a bullish candlestick.</li>
          <li>Set your entry price (where you’ll open the trade).</li>
          <li>Set a stop-loss to limit losses (e.g., 20 pips below entry for a buy trade).</li>
          <li>Set a take-profit for your target (e.g., 40 pips above entry for a 1:2 risk-to-reward ratio).</li>
          <li>Calculate your lot size to risk only 1–2% of your account. Example: With a $1,000 account, risk $20 (0.01 lots for EUR/USD). Our backtesting software’s Trade Optimizer can help!</li>
          <MDBox my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
            src="/tradeopitimizer.png"
            alt="Trade Optimizer Example"
            style={{ maxWidth: '100%', height: 'auto' }}
        />
        </MDBox>
          <li>Record the trade in your journal: note the pair, timeframe, entry, stop-loss, take-profit, and why you took the trade.</li>
          <li>Test your setup in our backtesting software to ensure it matches your strategy rules.</li>
        </ul>
        <MDTypography variant="body1" color="text" mb={2}>
          Next steps: Use our backtesting software to practice more trades and refine your strategy. Once confident, practice in a demo account to build experience. Watch strategy videos on YouTube or join trading communities to learn more. Stay disciplined, and you’re on your way to trading confidently! 
          Once you have profitability on a demo account, you can move onto real live money trading - but only risk what you can afford to lose.
        </MDTypography>
      </MDBox>
    ),
    practice: true,
    quiz: [
        {
          question: 'What is the first step in the trade setup checklist?',
          options: ['Record the trade in your journal', 'Set a stop-loss', 'Open backtesting software'],
          answer: 2 // Correct: Open backtesting software
        },
        {
          question: 'What timeframe is recommended for beginners in the practice section?',
          options: ['1-day (D1)', '1-minute (M1)', '1-hour (H1)'],
          answer: 2 // Correct: 1-hour (H1)
        },
        {
          question: 'What should you analyze on a chart for a trade setup?',
          options: ['Broker fees', 'Trends, key levels, or chart patterns', 'Economic news only'],
          answer: 1 // Correct: Trends, key levels, or chart patterns
        },
        {
          question: 'How much should you risk per trade with a $1,000 account?',
          options: ['$200', '$50-$100', '$10-$20'],
          answer: 2 // Correct: $10-$20
        },
        {
          question: 'What is the purpose of backtesting 50–100 trades?',
          options: ['To copy other traders', 'To ensure your strategy is consistent', 'To trade live immediately',],
          answer: 1 // Correct: To ensure your strategy is consistent
        },
        {
          question: 'What should you record in your trade journal?',
          options: ['Pair, timeframe, entry, stop-loss, take-profit, and reasons', 'Only profits', 'News events only'],
          answer: 0 // Correct: Pair, timeframe, entry, stop-loss, take-profit, and reasons
        },
        {
          question: 'What is the purpose of a demo account?',
          options: ['Practice with virtual money', 'Calculate taxes', 'Trade with real money'],
          answer: 0 // Correct: Practice with virtual money
        },
        {
          question: 'What does a 1:2 risk-to-reward ratio mean in the example trade?',
          options: ['Risk 20 pips to gain 20 pips', 'Risk 40 pips to gain 20 pips', 'Risk 20 pips to gain 40 pips'],
          answer: 2 // Correct: Risk 20 pips to gain 40 pips
        }
      ]
  },
];

const Section = ({ section, index, onQuizComplete, completed }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false); // Track quiz completion
  const [practiceCompleted, setPracticeCompleted] = useState(completed);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  const handleAnswerSelect = (questionIndex, optionIndex) => {
    console.log(`Selected answer for question ${questionIndex}: ${optionIndex}`);
    setSelectedAnswers({ ...selectedAnswers, [questionIndex]: optionIndex });
  };

  const handleQuizSubmit = () => {
    if (!section.quiz || Object.keys(selectedAnswers).length !== section.quiz.length) {
      alert('Please answer all questions before submitting.');
      return;
    }
    setQuizSubmitted(true);
    const correct = section.quiz.every((q, i) => selectedAnswers[i] === q.answer);
    if (correct) {
      setQuizCompleted(true); // Mark quiz as completed
      setShowQuiz(false);
      setQuizSubmitted(false);
      setSelectedAnswers({});
      if (!section.practice) {
        onQuizComplete(index); // For non-practice sections, complete immediately
        // Delay collapsing the section to show the congratulatory message
        setTimeout(() => {
          setIsOpen(false); // Collapse after 3 seconds
          setPracticeCompleted(true);
        }, 3000);
      } else {
        // For practice section, wait for backtesting completion
        setIsOpen(true); // Keep open to encourage backtesting
      }
    }
  };

  const handlePracticeComplete = () => {
    if (!quizCompleted && section.practice) {
      alert('Please complete the quiz before marking backtesting as complete.');
      return;
    }
    onQuizComplete(index); // Mark section as complete in Firebase
    setIsOpen(false); // Collapse the section
    setPracticeCompleted(true); // Mark as completed
  };
  return (
    <Card sx={{ mb: 2, backgroundColor: theme.palette.background.default }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <MDTypography variant="h6" fontWeight="bold">
          {section.title}
          {completed && <CheckCircleIcon color="success" sx={{ ml: 1, fontSize: '1.2rem' }} />}
        </MDTypography>
        <IconButton sx={{ color: iconColor }} onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </MDBox>
      <Collapse in={isOpen}>
        <MDBox p={2}>
          {section.content}
          {section.quiz && (
            <MDButton
              variant="gradient"
              color="info"
              onClick={() => setShowQuiz(true)}
              sx={{ mt: 2, mr: 2 }}
              disabled={quizCompleted || completed} // Disable after quiz or section completion
            >
              Take Quiz
            </MDButton>
          )}
          {section.practice && (
            <MDButton
              variant="gradient"
              color="success"
              onClick={handlePracticeComplete}
              sx={{ mt: 2 }}
              disabled={completed || !quizCompleted} // Disable until quiz is completed
            >
              I Completed Backtesting
            </MDButton>
          )}
                    {quizCompleted && (!section.practice || (section.practice && !practiceCompleted)) && (
            <MDTypography variant="body1" color="success" sx={{ mt: 2 }}>
              Congratulations, you've completed this section's quiz!
            </MDTypography>
          )}
        </MDBox>
      </Collapse>
      {showQuiz && (
        <MDBox
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',

            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            p: 2,
          }}
        >
          <Card sx={{ p: 3, maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto' }}>
            <MDTypography variant="h6" fontWeight="bold" mb={2}>
              {section.title} Quiz
            </MDTypography>
            {section.quiz.map((q, i) => (
              <MDBox key={i} mb={2}>
                <MDTypography variant="body1" fontWeight="medium">
                  {q.question}
                </MDTypography>
                <RadioGroup
                  value={selectedAnswers[i] ?? ''}
                  onChange={(e) => handleAnswerSelect(i, parseInt(e.target.value))}
                >
                  {q.options.map((option, j) => (
                    <FormControlLabel
                      key={j}
                      value={j}
                      control={<Radio />}
                      label={option}
                    />
                  ))}
                </RadioGroup>
                {quizSubmitted && (
                  <MDTypography
                    variant="body2"
                    color={selectedAnswers[i] === q.answer ? 'success' : 'error'}
                  >
                    {selectedAnswers[i] === q.answer
                      ? 'Correct!'
                      : `Incorrect. Correct answer: ${q.options[q.answer]}`}
                  </MDTypography>
                )}
              </MDBox>
            ))}
            <MDBox display="flex" justifyContent="space-between">
              <Tooltip
                title={
                  Object.keys(selectedAnswers).length !== section.quiz.length
                    ? 'Answer all questions to submit'
                    : ''
                }
              >
                <span>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleQuizSubmit}
                    disabled={Object.keys(selectedAnswers).length !== section.quiz.length}
                  >
                    Submit Quiz
                  </MDButton>
                </span>
              </Tooltip>
              <MDButton
                variant="text"
                color="dark"
                onClick={() => {
                  setShowQuiz(false);
                  setQuizSubmitted(false);
                  setSelectedAnswers({});
                }}
              >
                Close
              </MDButton>
            </MDBox>
          </Card>
        </MDBox>
      )}
    </Card>
  );
};

const LearnForex = () => {
  const [completedSections, setCompletedSections] = useState([]);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];
  const auth = getAuth();

  useEffect(() => {
    const fetchCompletedSections = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const userDocRef = doc(db, 'Users', user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const data = userDoc.data();
            setCompletedSections(data.completedSections || []);
          }
        }
      } catch (error) {
        console.error('Error fetching completed sections:', error);
      }
    };
    fetchCompletedSections();
  }, [auth]);

  const handleQuizComplete = async (index) => {
    if (!completedSections.includes(index)) {
      const updatedSections = [...completedSections, index];
      setCompletedSections(updatedSections);
      try {
        const user = auth.currentUser;
        if (user) {
          const userDocRef = doc(db, 'Users', user.uid);
          await setDoc(userDocRef, { completedSections: updatedSections }, { merge: true });
        }
      } catch (error) {
        console.error('Error saving completed section:', error);
      }
    }
  };

  const progress = (completedSections.length / sections(theme).length) * 100;

  return (
    <MDBox sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', p: 3 }}>
      <Grid container justifyContent="center">
        <Grid item xs={12} md={10} lg={8}>
          <Card sx={{ p: 3, mb: 3 }}>
            <MDTypography variant="h4" fontWeight="bold" textAlign="center" mb={2}>
              Learn Forex Trading
            </MDTypography>
            <MDTypography variant="body1" color="text" textAlign="center" mb={2}>
              Progress: {completedSections.length}/{sections(theme).length} Sections Completed
            </MDTypography>
            <MDBox sx={{ bgcolor: 'grey.300', borderRadius: '4px', height: '8px', mb: 3 }}>
              <MDBox
                sx={{
                  bgcolor: 'info.main',
                  width: `${progress}%`,
                  height: '100%',
                  borderRadius: '4px',
                }}
              />
            </MDBox>
          </Card>
          {sections(theme).map((section, index) => (
            <Section
              key={index}
              section={section}
              index={index}
              onQuizComplete={handleQuizComplete}
              completed={completedSections.includes(index)}
            />
          ))}
          {completedSections.length === sections(theme).length && (
            <Card sx={{ p: 3, mt: 3, textAlign: 'center' }}>
              <MDTypography variant="h5" color="success" fontWeight="bold">
                Congratulations!
              </MDTypography>
              <MDTypography variant="body1" color="text">
                You’ve completed all sections! You’re ready to start learning strategies from traders such as: Strong Money Forex, Ryan Cruise and Ara Kerbabian. Once you have a strategy you like, you can continue practising trading with our backtesting software. Backtest your strategies over 50–100 trades, then move to a demo account to apply your skills carefully.
                Remember frequent backtesting sessions keep your edge sharp and confidence high.
              </MDTypography>
            </Card>
          )}
        </Grid>
      </Grid>
    </MDBox>
  );
};

export default LearnForex;